import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import { RoomsOrderBy, SortDirection } from 'garaje/graphql/generated/roomba-types';
import type {
  GetCompanyQuery,
  PaginationInput,
  RoomsPageFilterInput,
  RoomsSortOrder,
} from 'garaje/graphql/generated/roomba-types';
import type TakeoverModel from 'garaje/models/takeover';
import type AjaxService from 'garaje/services/ajax';
import type FeatureConfigService from 'garaje/services/feature-config';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type RoomsPollingService from 'garaje/services/rooms-polling';
import type StateService from 'garaje/services/state';
import type { ResourceAmenityType } from 'garaje/types/generic-resources/ResourceTypeConfig';
import { GENERIC_RESOURCE_ICON } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';

import type ResourceTypeIndexController from './controller';

export interface ResourcesResourceTypeIndexRouteModel {
  resourceTypeLabel: string;
  resourceTypeId: string;
  resourceTypeIcon: GENERIC_RESOURCE_ICON;
  [x: string]: unknown;
}

interface RoomInventoryModel {
  company: GetCompanyQuery['company'];
  totalCount: number;
  takeovers: TakeoverModel[];
}

export interface DeskInventoryModel {
  desks: {
    id: string;
    floor: string;
    name: string;
    enabled: string;
    neighborhood: string;
    amenities: string;
    assignedToId?: string;
    assignedToName?: string;
    assignedToEmail?: string;
    assignedToAvatar?: string;
  }[];
  totalCount: number;
  downloadCsvUrl: string;
}

interface AmenitiesDataModel {
  resourceTypeAmenityId: number;
  name: string;
  resourceTypeId: number;
  relationId: string;
}
export interface ResourceInventoryModel {
  amenitiesMap: { [amenityId: string]: string };
  amenities: ResourceAmenityType[];
}

export default class ResourcesResourceTypeIndexRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare featureConfig: FeatureConfigService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare roomsPolling: RoomsPollingService;
  @service declare ajax: AjaxService;

  async model(): Promise<ResourcesResourceTypeIndexRouteModel> {
    const resourceTypeId = this.currentResourceTypeId;

    let resourceTypeLabel = 'Resource';
    let resourceTypeIcon = GENERIC_RESOURCE_ICON.NO_ICON;
    let model: { [x: string]: unknown } = {};

    if (resourceTypeId === '1') {
      resourceTypeLabel = 'Desk';
      resourceTypeIcon = GENERIC_RESOURCE_ICON.DESK;
      model = (await this.loadDeskInventory()) as unknown as { [x: string]: unknown };
    }

    if (resourceTypeId === '2') {
      resourceTypeLabel = 'Parking';
      resourceTypeIcon = GENERIC_RESOURCE_ICON.PARKING;
      model = (await this.createResourceInventoryModel()) as unknown as { [x: string]: unknown };
    }

    if (resourceTypeId === '3') {
      resourceTypeLabel = 'Room';
      resourceTypeIcon = GENERIC_RESOURCE_ICON.ROOM;
      model = (await this.loadRoomInventory()) as unknown as { [x: string]: unknown };
    }

    return {
      resourceTypeLabel,
      resourceTypeId,
      resourceTypeIcon,
      ...model,
    };
  }

  get currentResourceTypeId(): string {
    const { resource_type_id } = this.paramsFor('resources.resource-type') as {
      resource_type_id: string;
    };

    return resource_type_id;
  }

  get downloadCsvUrl(): string {
    return urlBuilder.rms.downloadDesksCsv(this.state.currentLocation.id);
  }

  async createResourceInventoryModel(): Promise<ResourceInventoryModel> {
    const getAmenitiesUrl = urlBuilder.rms.amenities(Number(this.currentResourceTypeId), this.state.currentLocation.id);

    const response: { data: AmenitiesDataModel[] } = await this.ajax.request(getAmenitiesUrl, {
      type: 'GET',
      contentType: 'application/json',
    });

    const amenitiesMap = response.data.reduce((acc: { [key: string]: string }, amenity) => {
      acc[amenity.resourceTypeAmenityId] = amenity.name;
      return acc;
    }, {});

    return {
      amenitiesMap,
      amenities: response.data,
    };
  }

  async loadDeskInventory(): Promise<DeskInventoryModel> {
    const currentLocationEnabledDesks = this.state.currentLocationEnabledDesks;

    if (!this.featureConfig.isEnabled('desks') || !currentLocationEnabledDesks?.active) {
      void this.router.transitionTo('resources.resource-type.landing', 1);
    }

    const allDesks = await this.store.query('desk', {
      filter: {
        'location-id': this.state.currentLocation.id,
      },
      include: 'assignments,amenities',
    });

    const desks = await Promise.all(
      allDesks.slice().map(async (desk) => {
        const [floor, neighborhood, amenities] = await Promise.all([desk.floor, desk.neighborhood, desk.amenities]);

        return {
          id: desk.id,
          floor: floor?.name || '',
          name: desk.name,
          enabled: desk.enabled ? 'Yes' : 'No',
          neighborhood: neighborhood?.name || '',
          amenities: amenities
            .slice()
            .map((amenity) => amenity.name)
            .join(', '),
          assignedTo: desk.assignedTo,
        };
      }),
    );

    return {
      desks,
      totalCount: desks.length,
      downloadCsvUrl: this.downloadCsvUrl,
    };
  }

  async loadRoomInventory(): Promise<RoomInventoryModel> {
    await this.roomsPolling.fetchRooms.perform();

    const sort: RoomsSortOrder = { by: RoomsOrderBy.DisplayName, direction: SortDirection.Asc };

    const filter: RoomsPageFilterInput = {
      locationId: { _eq: this.state.currentLocation.id, _in: null, _is_null: null, _neq: null, _nin: null },
      buildingId: null,
      buildingName: null,
      displayName: null,
      floorName: null,
    };

    const pagination: PaginationInput = {
      limit: 1,
      offset: 0,
    };

    const takeoversPromise = this.featureConfig.isEnabled('communications.takeovers')
      ? this.store
          .query('takeover', {
            filter: {
              location: this.state.currentLocation.id,
            },
          })
          .then((takeovers) => takeovers.slice())
      : Promise.resolve([]);

    const [company, { totalCount }, takeovers] = await Promise.all([
      this.roombaGraphql.getCompany(),
      this.roombaGraphql.getRoomsPaginated(filter, sort, pagination),
      takeoversPromise,
    ]);

    return {
      company,
      totalCount,
      takeovers,
    };
  }

  setupController(
    controller: ResourceTypeIndexController,
    model: ResourcesResourceTypeIndexRouteModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    void controller.loadEmployeesTask.perform();
    void controller.loadFloorsTask.perform();
  }
}
